import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, Bike, Zap, Target, TrendingUp, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";

const CyclingVisionPage = ({ token }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamRef = useRef(null);
  const elapsedRef = useRef(0);
  const lastValidRef = useRef(null);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sports/cycling/demo/stage_1?elapsed=${elapsedRef.current}`, { headers: authHeaders });
      setData(res.data);
      lastValidRef.current = res.data;
      elapsedRef.current += 1.2;
    } catch (e) {
      if (lastValidRef.current) setData(lastValidRef.current);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // No auto-start — presenter clicks Start when ready

  const toggleStream = () => {
    if (isStreaming) {
      clearInterval(streamRef.current);
      setIsStreaming(false);
    } else {
      setIsStreaming(true);
      streamRef.current = setInterval(fetchData, 1200);
    }
  };

  useEffect(() => () => { if (streamRef.current) clearInterval(streamRef.current); }, []);

  const d = data;
  const banner = d?.banner;

  return (
    <div className="min-h-screen bg-[#090a0d] text-white flex flex-col" data-testid="cycling-vision-page">
      {/* Header */}
      <header className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-4 h-12 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Bike className="w-4 h-4 text-[#A855F7]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Race Intelligence</span>
          <span className="text-[#A855F7] text-xs font-bold">— Cycling Mode</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={toggleStream} size="sm" className={`h-7 text-xs ${isStreaming ? 'bg-red-600 text-white' : 'bg-[#10B981] text-white'}`} data-testid="cycling-stream-btn">
            {isStreaming ? "Stop" : "Start"} Simulation
          </Button>
          <Button onClick={() => navigate("/")} variant="ghost" size="sm" className="text-white/40 hover:text-white h-7" data-testid="cycling-back-hub">
            <ArrowLeft className="w-3 h-3 mr-1" /> Hub
          </Button>
        </div>
      </header>

      {d ? (
        <div className="flex-1 flex flex-col p-6 max-w-5xl mx-auto w-full">
          <div className="grid grid-cols-3 gap-4 mb-6" data-testid="cycling-banner">
            <MetricCard
              label="Peloton Pressure"
              value={banner?.peloton_pressure}
              suffix="%"
              colour={banner?.peloton_pressure > 60 ? "#EF4444" : "#F59E0B"}
              icon={Target}
            />
            <MetricCard
              label="Breakaway Strength"
              value={banner?.breakaway_strength}
              suffix="%"
              colour={banner?.breakaway_strength > 70 ? "#10B981" : "#F59E0B"}
              icon={Zap}
            />
            <MetricCard
              label="Chase Momentum"
              value={banner?.chase_momentum}
              colour={banner?.chase_momentum === "Building" ? "#10B981" : "#F59E0B"}
              icon={TrendingUp}
              isText
            />
          </div>

          {/* Race Visual */}
          <div className="flex-1 relative rounded-2xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden mb-6" data-testid="cycling-visual">
            {/* Track line */}
            <svg className="w-full h-full absolute inset-0" viewBox="0 0 800 300" preserveAspectRatio="none">
              {/* Road */}
              <rect x="0" y="110" width="800" height="80" fill="rgba(255,255,255,0.03)" rx="4" />
              <line x1="0" y1="150" x2="800" y2="150" stroke="rgba(255,255,255,0.06)" strokeDasharray="20 10" />

              {/* Riders */}
              {d.riders?.map((rider, i) => {
                const isBreakaway = rider.type === "breakaway";
                const baseX = isBreakaway ? 520 + i * 35 : 180 + (i - 3) * 25;
                const baseY = 130 + (i % 3) * 25;
                const colour = isBreakaway ? "#A855F7" : "#3B82F6";

                return (
                  <g key={rider.id}>
                    {/* Rider dot */}
                    <circle cx={baseX} cy={baseY} r={6} fill={colour} opacity={0.9} />
                    {/* Rider tag */}
                    <rect x={baseX + 10} y={baseY - 8} width={50} height={16} rx={3} fill="rgba(0,0,0,0.8)" />
                    <text x={baseX + 14} y={baseY + 3} fill="white" fontSize="8" fontWeight="bold" fontFamily="monospace">
                      R{rider.id}
                    </text>
                    <text x={baseX + 36} y={baseY + 3} fill={rider.state === "Strong" ? "#10B981" : "#EF4444"} fontSize="7" fontFamily="monospace">
                      {rider.state === "Strong" ? "STR" : "FAD"}
                    </text>
                  </g>
                );
              })}

              {/* Gap indicator */}
              <line x1="380" y1="125" x2="380" y2="175" stroke="#F97316" strokeWidth="1" strokeDasharray="4 3" />
              <rect x="355" y="140" width="50" height="18" rx="3" fill="rgba(249,115,22,0.2)" />
              <text x="366" y="153" fill="#F97316" fontSize="9" fontWeight="bold" fontFamily="monospace">
                {d.gap_seconds}s
              </text>

              {/* Labels */}
              <text x="160" y="210" fill="rgba(255,255,255,0.15)" fontSize="10" fontFamily="monospace">PELOTON</text>
              <text x="520" y="210" fill="rgba(255,255,255,0.15)" fontSize="10" fontFamily="monospace">BREAKAWAY</text>

              {/* KM marker */}
              <text x="700" y="250" fill="rgba(255,255,255,0.2)" fontSize="10" fontFamily="monospace">{d.km_to_go} km to go</text>
            </svg>
          </div>

          {/* Key Insight */}
          <div className="p-4 rounded-xl border border-[#A855F7]/30 bg-[#A855F7]/5" data-testid="cycling-insight">
            <div className="text-[#A855F7] text-[10px] uppercase tracking-widest font-bold mb-1">Key Insight</div>
            <p className="text-white text-sm">{d.key_insight}</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center flex-col gap-3" data-testid="cycling-loading">
          <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
          <span className="text-white/40 text-xs font-mono">Initialising cycling data...</span>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// METRIC CARD
// =============================================================================

const MetricCard = ({ label, value, suffix = "", colour, icon: Icon, isText }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-[#0d0f12]/80 text-center">
    <div className="flex items-center justify-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color: colour }} />
      <span className="text-white/40 text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    {isText ? (
      <div className="text-2xl font-bold font-heading" style={{ color: colour }}>{value}</div>
    ) : (
      <div className="text-3xl font-bold font-heading" style={{ color: colour }}>{Math.round(value || 0)}<span className="text-lg">{suffix}</span></div>
    )}
  </div>
);

export default CyclingVisionPage;
